import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { supabaseChatService } from '../../services/supabaseChatService';
import { useAdminMessages } from '../../hooks/useAdminMessages';
import { supabase } from '../../services/supabase';

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background: var(--surfaceVariant);
  display: flex;
  flex-direction: column;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--outline);
  background: var(--surface);
`;

const HeaderAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 999px;
  overflow: hidden;
  background: var(--outline);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--onSurfaceVariant);
  font-weight: 700;
`;

const HeaderAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeaderName = styled.div`
  font-weight: 600;
  color: var(--onSurface);
`;

const Bubble = styled.div<{ me: boolean }>`
  max-width: 60%;
  margin-bottom: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 18px;
  background: ${({ me }) => (me ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ me }) => (me ? 'var(--onPrimary)' : 'var(--onSurface)')};
  align-self: ${({ me }) => (me ? 'flex-end' : 'flex-start')};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  position: relative;

  &:hover span {
    opacity: 1;
  }
`;

const MessageImageLink = styled.a`
  display: block;
`;

const MessageImage = styled.img`
  display: block;
  max-width: min(320px, 100%);
  max-height: 320px;
  border-radius: 12px;
  object-fit: cover;
  margin-bottom: 0.5rem;
`;

const Timestamp = styled.span`
  position: absolute;
  bottom: -16px;
  right: 4px;
  font-size: 10px;
  color: var(--onSurfaceVariant);
  opacity: 0;
  transition: opacity 0.2s;
`;

const Composer = styled.form`
  padding: 0.75rem;
  border-top: 1px solid var(--outline);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ComposerRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
`;

const Input = styled.textarea`
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 6px;
  resize: none;
  min-height: 42px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const AttachButton = styled.button`
  flex-shrink: 0;
  background: var(--surface);
  color: var(--onSurface);
  border: 1px solid var(--outline);
  padding: 0 0.85rem;
  min-height: 42px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SendButton = styled.button`
  flex-shrink: 0;
  background: var(--primary);
  color: white;
  border: none;
  padding: 0 1rem;
  min-height: 42px;
  border-radius: 6px;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PreviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.5rem 0.65rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surfaceVariant);
`;

const PreviewThumb = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

const PreviewMeta = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const PreviewName = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--onSurface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PreviewHint = styled.span`
  font-size: 0.72rem;
  color: var(--onSurfaceVariant);
`;

const RemovePreviewButton = styled.button`
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: var(--onSurfaceVariant);
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  padding: 0.25rem;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SendError = styled.div`
  font-size: 0.78rem;
  color: var(--error, #b42318);
`;

interface Props {
  otherUserId: string | null;
}

const MAX_CHAT_IMAGE_BYTES = 8 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

export const AdminChatWindow: React.FC<Props> = ({ otherUserId }) => {
  const { user } = useAuth();
  const currentId = user?.id || '';
  const queryClient = useQueryClient();
  const { data: messages } = useAdminMessages(currentId, otherUserId || '');
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const imagePreviewUrlRef = useRef<string | null>(null);
  const [text, setText] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [otherUser, setOtherUser] = useState<{
    id: string;
    full_name: string | null;
    avatar: string | null;
  } | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    return () => {
      if (imagePreviewUrlRef.current) {
        URL.revokeObjectURL(imagePreviewUrlRef.current);
        imagePreviewUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setText('');
    setSendError(null);
    if (imagePreviewUrlRef.current) {
      URL.revokeObjectURL(imagePreviewUrlRef.current);
      imagePreviewUrlRef.current = null;
    }
    setImageFile(null);
    setImagePreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  }, [otherUserId]);

  useEffect(() => {
    if (!currentId || !otherUserId) return;
    const hasUnread = messages.some(
      (message) =>
        message.receiver_id === currentId &&
        message.sender_id === otherUserId &&
        !message.read_at
    );
    if (!hasUnread) return;

    supabaseChatService
      .markMessagesAsRead(currentId, otherUserId)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ['admin-chat-users', currentId],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: ['admin-chat-messages', currentId, otherUserId],
        });
      })
      .catch(() => undefined);
  }, [currentId, otherUserId, messages, queryClient]);

  useEffect(() => {
    if (!otherUserId) {
      setOtherUser(null);
      return;
    }
    const loadRecipient = async () => {
      try {
        const { data } = await supabase
          .from('users')
          .select('id, full_name, avatar')
          .eq('id', otherUserId)
          .maybeSingle();
        if (data) {
          setOtherUser({
            id: data.id,
            full_name: data.full_name ?? null,
            avatar: data.avatar ?? null,
          });
        } else {
          setOtherUser(null);
        }
      } catch {
        setOtherUser(null);
      }
    };
    loadRecipient();
  }, [otherUserId]);

  const clearSelectedImage = () => {
    if (imagePreviewUrlRef.current) {
      URL.revokeObjectURL(imagePreviewUrlRef.current);
      imagePreviewUrlRef.current = null;
    }
    setImageFile(null);
    setImagePreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) return;

    if (!ACCEPTED_IMAGE_TYPES.has(file.type)) {
      setSendError('Please choose a JPEG, PNG, WebP, or GIF image.');
      event.target.value = '';
      return;
    }

    if (file.size > MAX_CHAT_IMAGE_BYTES) {
      setSendError('Please choose an image smaller than 8MB.');
      event.target.value = '';
      return;
    }

    setSendError(null);
    if (imagePreviewUrlRef.current) {
      URL.revokeObjectURL(imagePreviewUrlRef.current);
    }
    const previewUrl = URL.createObjectURL(file);
    imagePreviewUrlRef.current = previewUrl;
    setImagePreviewUrl(previewUrl);
    setImageFile(file);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if ((!trimmed && !imageFile) || !otherUserId || sending) return;

    setSending(true);
    setSendError(null);
    try {
      const ok = await supabaseChatService.sendMessage(
        currentId,
        otherUserId,
        trimmed,
        imageFile || undefined
      );
      if (!ok) {
        setSendError('Could not send message. Please try again.');
        return;
      }

      setText('');
      clearSelectedImage();
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }
      queryClient.invalidateQueries({
        queryKey: ['admin-chat-messages', currentId, otherUserId],
      });
      queryClient.invalidateQueries({
        queryKey: ['admin-chat-users', currentId],
        exact: false,
      });
    } finally {
      setSending(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(event.target.value);
    const el = event.target;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  if (!otherUserId) {
    return <div style={{ padding: '1rem' }}>Select a conversation</div>;
  }

  const canSend = Boolean(text.trim() || imageFile) && !sending;

  return (
    <Container>
      <HeaderRow>
        <HeaderAvatar>
          {otherUser?.avatar ? (
            <HeaderAvatarImage src={otherUser.avatar} alt={otherUser.full_name ?? 'User avatar'} />
          ) : (
            (otherUser?.full_name || 'U').charAt(0).toUpperCase()
          )}
        </HeaderAvatar>
        <HeaderName>{otherUser?.full_name ?? 'User'}</HeaderName>
      </HeaderRow>
      <MessagesContainer>
        {messages.map((m) => {
          const imageUrl = (m.image_url ?? '').trim();
          const textContent = (m.content ?? '').trim();
          return (
            <Bubble key={m.id} me={m.sender_id === currentId}>
              {imageUrl ? (
                <MessageImageLink href={imageUrl} target="_blank" rel="noopener noreferrer">
                  <MessageImage src={imageUrl} alt="Message attachment" />
                </MessageImageLink>
              ) : null}
              {textContent}
              <Timestamp>
                {new Date(m.created_at).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Timestamp>
            </Bubble>
          );
        })}
        <div ref={bottomRef} />
      </MessagesContainer>
      <Composer onSubmit={handleSend}>
        {imagePreviewUrl && imageFile ? (
          <PreviewRow>
            <PreviewThumb src={imagePreviewUrl} alt="Selected image preview" />
            <PreviewMeta>
              <PreviewName title={imageFile.name}>{imageFile.name}</PreviewName>
              <PreviewHint>Ready to send with your message</PreviewHint>
            </PreviewMeta>
            <RemovePreviewButton
              type="button"
              aria-label="Remove selected image"
              onClick={clearSelectedImage}
              disabled={sending}
            >
              ×
            </RemovePreviewButton>
          </PreviewRow>
        ) : null}
        {sendError ? <SendError>{sendError}</SendError> : null}
        <ComposerRow>
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleImageSelect}
            disabled={sending}
          />
          <AttachButton
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            aria-label="Attach image"
          >
            {imageFile ? 'Change' : 'Image'}
          </AttachButton>
          <Input
            ref={inputRef}
            value={text}
            onChange={handleInputChange}
            placeholder={imageFile ? 'Add a caption (optional)' : 'Type a message'}
            rows={2}
            disabled={sending}
          />
          <SendButton type="submit" disabled={!canSend}>
            {sending ? 'Sending…' : 'Send'}
          </SendButton>
        </ComposerRow>
      </Composer>
    </Container>
  );
};
